import { useRef, useState } from 'react';
import GradientIcon from '@/components/common/GradientIcon';
import { solutionTabs, type Solution, type SolutionVisual } from '@/mocks/landing';
import SectionTitle from './SectionTitle';

/** Small UI illustration inside each solution card. */
function Visual({ kind }: { kind: SolutionVisual }) {
  const card = 'rounded-xl bg-white/90 shadow-[0_8px_20px_-10px_rgba(31,35,41,0.25)]';
  switch (kind) {
    case 'chart':
      return (
        <div className={`${card} p-3`}>
          <div className="text-[10px] text-[#8f959e]">Points earned · 7 days</div>
          <div className="mt-2 flex items-end gap-1.5 h-14">
            {[40, 55, 35, 70, 60, 85, 75].map((h, i) => (
              <span key={i} className="flex-1 rounded-t bg-gradient-to-t from-[#2f6bff] to-[#9db7ff]" style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>
      );
    case 'ring':
      return (
        <div className={`${card} p-3 flex items-center gap-3`}>
          <div className="w-14 h-14 rounded-full p-1.5 bg-[conic-gradient(#ff4d8d_0_68%,#ffe3ee_68%_100%)]">
            <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[11px] font-bold text-[#d9348a]">68%</div>
          </div>
          <div className="text-[10px] text-[#646a73] leading-relaxed">
            Redeemed this month
            <div className="font-heading font-bold text-sm text-[#1f2329]">4,820</div>
          </div>
        </div>
      );
    case 'list':
      return (
        <div className={`${card} p-3 space-y-2`}>
          {['Coffee voucher', 'Tote bag', '10% off'].map((t, i) => (
            <div key={t} className="flex items-center gap-2 text-[10px] text-[#1f2329]">
              <span className={`w-2 h-2 rounded-full ${['bg-[#7b5cff]', 'bg-[#ff4d8d]', 'bg-[#00c2a8]'][i]}`} />
              {t}
              <span className="ml-auto text-[#8f959e]">{[1200, 2500, 800][i]} pts</span>
            </div>
          ))}
        </div>
      );
    case 'chat':
      return (
        <div className="space-y-2">
          <div className={`${card} px-3 py-2 text-[10px] text-[#1f2329] w-4/5`}>Hi Mia, you earned 150 pts 🎉</div>
          <div className="rounded-xl bg-[#2f6bff] text-white px-3 py-2 text-[10px] w-3/5 ml-auto">Redeem coffee!</div>
          <div className={`${card} px-3 py-2 text-[10px] text-[#1f2329] w-2/3`}>Done. Show code LB-82K</div>
        </div>
      );
    case 'stat':
      return (
        <div className={`${card} p-3`}>
          <div className="text-[10px] text-[#8f959e]">Repeat revenue</div>
          <div className="font-heading font-bold text-xl text-[#1f2329]">$75,800</div>
          <div className="text-[10px] text-[#0e9f7e]">▲ 43% vs last quarter</div>
          <svg viewBox="0 0 100 24" className="mt-1 w-full h-6">
            <polyline fill="none" stroke="#00b2ff" strokeWidth="2" points="0,20 15,16 30,18 45,10 60,12 75,6 100,3" />
          </svg>
        </div>
      );
    case 'tiers':
      return (
        <div className={`${card} p-3 space-y-1.5`}>
          {[
            { n: 'Elite', w: '100%', c: 'from-[#2f51d6] to-[#7b5cff]' },
            { n: 'Pro', w: '70%', c: 'from-[#5b8cff] to-[#9db7ff]' },
            { n: 'Starter', w: '40%', c: 'from-[#a9c1ff] to-[#d6e2ff]' },
          ].map((t) => (
            <div key={t.n} className="text-[10px] text-[#646a73]">
              {t.n}
              <div className="h-1.5 mt-0.5 rounded-full bg-[#eef0f5]">
                <div className={`h-full rounded-full bg-gradient-to-r ${t.c}`} style={{ width: t.w }} />
              </div>
            </div>
          ))}
        </div>
      );
    case 'code':
      return (
        <div className="rounded-xl bg-[#1f2329] p-3 font-mono text-[9.5px] leading-relaxed text-[#9db7ff] shadow-lg">
          <div><span className="text-[#ff8ab3]">POST</span> /api/v1/earn</div>
          <div className="text-[#c9cdd4]">{'{ "member": "mem_8f3k2",'}</div>
          <div className="text-[#c9cdd4]">{'  "points": 150 }'}</div>
          <div className="text-[#5ee0b5]">→ 201 Created</div>
        </div>
      );
  }
}

function SolutionCard({ item }: { item: Solution }) {
  return (
    <div
      className={`snap-start shrink-0 w-[210px] md:w-[230px] h-[300px] rounded-2xl bg-gradient-to-b ${item.gradient} p-4 flex flex-col border border-white hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(47,81,214,0.45)] transition-all duration-300`}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-[#646a73]">
        <GradientIcon icon={item.icon} tone={item.tone} size="text-sm" />
        {item.tag}
      </div>
      <h3 className="mt-2 text-lg font-bold text-[#1f2329]">{item.title}</h3>
      <div className="mt-auto">
        <Visual kind={item.visual} />
      </div>
    </div>
  );
}

export default function Solutions() {
  const [tab, setTab] = useState(solutionTabs[0]?.id ?? '');
  const track = useRef<HTMLDivElement>(null);
  const active = solutionTabs.find((t) => t.id === tab) ?? solutionTabs[0];

  const scroll = (dir: 1 | -1) => track.current?.scrollBy({ left: dir * 480, behavior: 'smooth' });

  return (
    <section id="solutions" className="w-full bg-white py-14 md:py-20 overflow-hidden">
      <div className="flex justify-center mb-8">
        <div className="inline-flex p-1 rounded-full bg-[#f2f3f5]">
          {solutionTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setTab(t.id);
                track.current?.scrollTo({ left: 0 });
              }}
              className={`px-4 h-8 rounded-full text-xs md:text-sm font-medium transition-all cursor-pointer ${
                t.id === tab ? 'bg-[#1f2329] text-white shadow' : 'text-[#646a73] hover:text-[#1f2329]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <SectionTitle title="Proven playbooks from leading loyalty teams" />

      <div className="relative max-w-7xl mx-auto">
        <div ref={track} className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-4 md:px-12 pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {active?.items.map((item) => <SolutionCard key={item.title} item={item} />)}
        </div>
        <button
          type="button"
          aria-label="Previous"
          onClick={() => scroll(-1)}
          className="hidden md:flex absolute left-3 top-[130px] w-10 h-10 rounded-full bg-white shadow-md items-center justify-center text-[#1f2329] hover:text-[#2f51d6] cursor-pointer"
        >
          <i className="ri-arrow-left-line text-lg" />
        </button>
        <button
          type="button"
          aria-label="Next"
          onClick={() => scroll(1)}
          className="hidden md:flex absolute right-3 top-[130px] w-10 h-10 rounded-full bg-white shadow-md items-center justify-center text-[#1f2329] hover:text-[#2f51d6] cursor-pointer"
        >
          <i className="ri-arrow-right-line text-lg" />
        </button>
      </div>
    </section>
  );
}
